import * as yup from 'yup';
import {
  IntegrationAuthConfig,
  IntegrationAuthType,
  IntegrationConfigField,
} from '@aslanpay/portal-common-lib';

import {
  AUTH_FIELDS,
  AUTH_TYPE_OPTIONS,
  INTEGRATION_TYPE_OPTIONS,
  PROTOCOL_OPTIONS,
} from './constants';
import { ConfigEntry, ConnectionFormValues } from './types';

export const buildAuthConfig = (data: ConnectionFormValues): IntegrationAuthConfig => {
  const authConfig: Record<string, string> = { type: data.authType };
  AUTH_FIELDS[data.authType].forEach(({ name }) => {
    const value = data[name];
    if (typeof value === 'string' && value.length > 0) {
      authConfig[name] = value;
    }
  });
  return authConfig as unknown as IntegrationAuthConfig;
};

export const buildConfig = (
  entries?: ConfigEntry[],
  configValues?: Record<string, string>
): Record<string, string> | undefined => {
  const populated = (entries ?? []).filter((entry) => entry.key.trim().length > 0);
  const specValues = Object.entries(configValues ?? {}).filter(
    ([, value]) => value.trim().length > 0
  );
  if (populated.length === 0 && specValues.length === 0) {
    return undefined;
  }
  return {
    ...Object.fromEntries(populated.map((entry) => [entry.key.trim(), entry.value])),
    ...Object.fromEntries(specValues.map(([key, value]) => [key, value.trim()])),
  };
};

export const configToEntries = (config?: Record<string, string>): ConfigEntry[] =>
  Object.entries(config ?? {}).map(([key, value]) => ({ key, value }));

export interface ConfigFieldContext {
  protocol?: string;
  integrationTypes?: string[];
}

/**
 * The spec fields applying to the current connection: protocol-scoped fields only for connections of
 * that protocol, and type-scoped fields only once their integration type is selected/enabled. Fields
 * outside this set are neither rendered nor required, mirroring the backend rule.
 */
export const applicableConfigFields = (
  specFields: IntegrationConfigField[],
  { protocol, integrationTypes }: ConfigFieldContext
): IntegrationConfigField[] =>
  specFields.filter(
    (field) =>
      (!field.protocol || field.protocol === protocol) &&
      (!field.integrationType || (integrationTypes ?? []).includes(field.integrationType))
  );

/** The required applicable fields the current form state leaves blank, for submit-time validation. */
export const findMissingRequiredConfigFields = (
  specFields: IntegrationConfigField[],
  context: ConfigFieldContext,
  configValues?: Record<string, string>
): IntegrationConfigField[] =>
  applicableConfigFields(specFields, context).filter(
    (field) => field.required && !configValues?.[field.key]?.trim()
  );

interface SecretRequirementContext {
  // When editing, secrets already stored for the unchanged auth type may stay blank (blank = keep).
  originalAuthType?: IntegrationAuthType;
  configuredSecretFields?: string[];
}

const requiredWhenAuthTypeNeedsField = (
  field: keyof ConnectionFormValues,
  context: SecretRequirementContext
) =>
  yup.string().when('authType', (authType, fieldSchema) => {
    const type = authType.toString() as IntegrationAuthType;
    const authField = AUTH_FIELDS[type]?.find((f) => f.name === field);
    if (!authField?.required) {
      return fieldSchema;
    }
    const secretAlreadyStored =
      authField.secret &&
      type === context.originalAuthType &&
      !!context.configuredSecretFields?.includes(field as string);
    return secretAlreadyStored
      ? fieldSchema
      : fieldSchema.required(`${authField.label} is required`);
  });

const connectionShape = (context: SecretRequirementContext) => ({
  baseUrl: yup.string().url('Base URL must be a valid URL').required('Base URL is required'),
  configEntries: yup.array().of(
    yup.object().shape({
      key: yup.string().required('Key is required'),
      value: yup.string().required('Value is required'),
    })
  ),
  authType: yup
    .string()
    .oneOf(
      AUTH_TYPE_OPTIONS.map((option) => option.value),
      'Invalid auth type'
    )
    .required('Auth type is required'),
  headerName: requiredWhenAuthTypeNeedsField('headerName', context),
  apiKey: requiredWhenAuthTypeNeedsField('apiKey', context),
  token: requiredWhenAuthTypeNeedsField('token', context),
  username: requiredWhenAuthTypeNeedsField('username', context),
  password: requiredWhenAuthTypeNeedsField('password', context),
  tokenUrl: requiredWhenAuthTypeNeedsField('tokenUrl', context),
  clientId: requiredWhenAuthTypeNeedsField('clientId', context),
  clientSecret: requiredWhenAuthTypeNeedsField('clientSecret', context),
  refreshToken: requiredWhenAuthTypeNeedsField('refreshToken', context),
  scope: yup.string(),
});

export const registerSchema = yup.object().shape({
  provider: yup.string().required('Provider is required'),
  protocol: yup
    .string()
    .oneOf(
      PROTOCOL_OPTIONS.map((option) => option.value),
      'Invalid protocol'
    )
    .required('Protocol is required'),
  integrationTypes: yup
    .array()
    .of(yup.string().oneOf(INTEGRATION_TYPE_OPTIONS.map((option) => option.value)))
    .min(1, 'Select at least one integration type')
    .required('Select at least one integration type'),
  ...connectionShape({}),
});

export const buildEditSchema = (context: SecretRequirementContext) =>
  yup.object().shape(connectionShape(context));
